import { ApplicationElement, html } from '../application_element/element.js';
import { smartDateFormatter } from '../../lib/time_helpers.js';
import style from './style.lit.css';
import { LongTouchController } from '../../lib/lit/long_touch_controller.js';
import { htmlToText } from '../../lib/helpers.js';
import translations from './translations.js';

/**
 * Chat list item
 *
 * @prop uid - Chat ID
 * @prop name - Chat title
 * @prop href - Chat url
 * @prop kind - Chat type (options: direct, group, multi)
 * @prop timestamp - Last message created time
 * @prop active - Draws component in a active state.
 * @prop mute - Draws component in a muted state.
 * @prop count - Unread messages number
 * @prop message - Last message body
 * @prop messageOwnerId - Last message owner ID.
 * @prop messageOwnerName - Last message owner name.
 * @prop messageDeleted - Is last message deleted.
 */
export default class ChatListItem extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    uid: { type: Number },
    name: { type: String },
    href: { type: String },
    kind: { type: String, reflect: true },
    timestamp: { type: Date },
    active: { type: Boolean, reflect: true },
    mute: { type: Boolean, reflect: true },
    count: { type: Number, reflect: true },
    message: { type: String },
    messageOwnerId: { type: Number },
    messageOwnerName: { type: String },
    messageDeleted: { type: Boolean },
    messageAttachment: { type: Object },

    menuOpen: { type: Boolean, reflect: true },
  };

  longTouchController = new LongTouchController(this, {
    callback: () => this.menu.openMenu(),
  });

  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('js-chat-list-item');
    this.active = this.isActive();
    this.setAttribute('tabindex', 0);
  }

  get menu() {
    return this.shadowRoot.querySelector('[role="menu"]');
  }

  get finalHref() {
    return this.href || `/chats/${this.uid}/messages`;
  }

  onClick(e) {
    if (this.isLongTouchPending) return;

    if (e.composedPath().some(el => el.role === 'menu')) {
      e.preventDefault();
      return;
    }

    if (e.defaultPrevented) return;

    window.Turbolinks?.visit?.(this.finalHref, {
      action: this.isChatView ? 'replace' : 'advance',
    });
  }

  /** @internal */
  get formattedDate() {
    return this.timestamp ? smartDateFormatter(this.timestamp.getTime()) : null;
  }

  /** @internal */
  get isChatView() {
    return /chats\/\d+\/messages/.test(window.location.href);
  }

  /** @internal */
  get messageBody() {
    const plainBody = this.message
      ? htmlToText(this.message).slice(0, 180)
      : null;

    if (this.messageDeleted) {
      return this.t('deleted_body');
    } else if (this.messageAttachment && !plainBody) {
      return this.t('file_sent');
    }

    return plainBody;
  }

  /** @internal */
  get isCurrentUserMessageOwner() {
    return this.messageOwnerId === this.AppConfig.user?.id;
  }

  /** @internal */
  get shouldRenderLabel() {
    return this.isUnread(); //&& !this.active;
  }

  isUnread() {
    return this.count > 0;
  }

  isActive() {
    return this.activateByCurrentLink();
  }

  onMenuOpen() {
    this.menuOpen = true;
  }

  onMenuClose() {
    this.menuOpen = false;
  }

  static renderMenu(chatId) {
    return html` <div>Implement menu for Chat: ${chatId}</div> `;
  }

  renderMessageOwnerName() {
    return html`
      <span class="message-owner"
        >${this.isCurrentUserMessageOwner
          ? this.t('my_last_message_label')
          : this.messageOwnerName}:</span
      >
    `;
  }

  renderMessage() {
    return html`
      <div class=${`message${this.messageDeleted ? ' deleted' : ''}`}>
        ${this.kind !== 'direct' ? this.renderMessageOwnerName() : ''}
        <span class="message-body">${this.messageBody}</span>
      </div>
    `;
  }

  renderLabel() {
    return html`
      <div class="unread-count visible">
        <span>${this.t('new')}:</span> ${this.count}
      </div>
    `;
  }

  renderMenu() {
    return html`
      <nav
        @dropdown-opened=${this.onMenuOpen}
        @dropdown-closed=${this.onMenuClose}>
        ${this.constructor.renderMenu(this.uid)}
      </nav>
    `;
  }

  render() {
    const unreadClass = this.shouldRenderLabel ? 'unread' : '';

    return html`
      <div class="content ${unreadClass}">
        <slot name="avatar"></slot>
        <div class="info">
          <div class="top">
            <h2 class="title">${this.name}</h2>
            <div class="date">${this.formattedDate}</div>
          </div>
          ${this.messageBody ? this.renderMessage() : null}
        </div>
      </div>
      ${this.shouldRenderLabel ? this.renderLabel() : null} ${this.renderMenu()}
    `;
  }
}

ChatListItem.registerTagName('ef-chat-list-item');

customElements.get('ef-chat-list-item') ||
  customElements.define('ef-chat-list-item', ChatListItem);
